import { useCallback, useEffect, useRef, useState } from 'react';
import { useExam } from '../context/ExamContext';
import { IncidentType } from '../types/domain';
import { browserViolationToIncident, BrowserViolationKind, CandidateWarning, MAX_WARNINGS, nextWarning } from '../utils/browserWarnings';
import { calculateDurationSeconds, makeIncident } from '../utils/incidents';
import { useWarningAudio } from './useWarningAudio';

interface BrowserMonitorOptions {
  autoSubmitAfterFinalWarning?: boolean;
  onFinalWarning?: () => void;
}

export function useBrowserMonitor(enabled: boolean, startedAt?: string, options: BrowserMonitorOptions = {}) {
  const { addIncident } = useExam();
  const [warningCount, setWarningCount] = useState(0);
  const [latestWarning, setLatestWarning] = useState<CandidateWarning>();
  const [muted, setMuted] = useState(() => sessionStorage.getItem('examWarningMuted') === 'true');
  const {
    status: audioStatus,
    initialize: initializeAudio,
    play: playWarningSound,
    setMuted: setWarningMuted,
    dispose: disposeWarningAudio
  } = useWarningAudio();
  const [fullscreenRequired, setFullscreenRequired] = useState(false);
  const lastWarningAt = useRef(0);
  const lastWarningType = useRef<BrowserViolationKind>();
  const awayStartedAt = useRef<string>();
  const optionsRef = useRef(options);

  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  const emit = useCallback(
    (kind: BrowserViolationKind, confidence = 90, durationStart?: string, durationEnd?: string) => {
      const now = Date.now();
      const timestamp = new Date(now).toISOString();
      const elapsed = startedAt ? Math.max(0, Math.round((now - new Date(startedAt).getTime()) / 1000)) : 0;
      const type = browserViolationToIncident(kind);
      const durationSeconds = durationStart && durationEnd ? calculateDurationSeconds(durationStart, durationEnd) : undefined;
      addIncident(makeIncident(type, confidence, severityFor(type), elapsed, 'browser', undefined, { timestamp, startedAt: durationStart ?? timestamp, endedAt: durationEnd, durationSeconds }));

      setWarningCount((current) => {
        const decision = nextWarning(current, lastWarningType.current, lastWarningAt.current, kind, now);
        if (!decision.shouldWarn || !decision.warning) return current;
        lastWarningAt.current = now;
        lastWarningType.current = kind;
        setLatestWarning(decision.warning);
        playWarningSound();
        if ((decision.warning.warningNumber ?? 0) >= MAX_WARNINGS && optionsRef.current.autoSubmitAfterFinalWarning) {
          optionsRef.current.onFinalWarning?.();
        }
        return decision.warningCount;
      });
    },
    [addIncident, playWarningSound, startedAt]
  );

  useEffect(() => () => disposeWarningAudio(), [disposeWarningAudio]);

  useEffect(() => {
    sessionStorage.setItem('examWarningMuted', String(muted));
    setWarningMuted(muted);
  }, [muted, setWarningMuted]);

  useEffect(() => {
    if (!enabled) return;

    const markAway = (kind: BrowserViolationKind) => {
      if (!awayStartedAt.current) {
        awayStartedAt.current = new Date().toISOString();
        emit(kind);
      }
    };
    const closeAway = () => {
      if (!awayStartedAt.current) return;
      const started = awayStartedAt.current;
      awayStartedAt.current = undefined;
      emit('tab_switch', 96, started, new Date().toISOString());
    };
    const onBlur = () => markAway('window_blur');
    const onFocus = () => closeAway();
    const onVisibility = () => {
      if (document.hidden) markAway('visibility_hidden');
      else closeAway();
    };
    const onFullscreen = () => {
      const isOut = !document.fullscreenElement;
      setFullscreenRequired(isOut);
      if (isOut) emit('fullscreen_exit', 94);
    };
    const onCopy = (event: ClipboardEvent) => {
      event.preventDefault();
      emit('clipboard', 86);
    };
    const onPaste = (event: ClipboardEvent) => {
      event.preventDefault();
      emit('clipboard', 86);
    };
    const onContext = (event: MouseEvent) => {
      event.preventDefault();
      emit('keyboard_shortcut', 82);
    };
    const onKey = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      const modifier = event.ctrlKey || event.metaKey;
      if (modifier && ['+', '=', '-', '0'].includes(key)) {
        event.preventDefault();
        emit('attempted_zoom', 88);
      } else if ((modifier || event.altKey) && ['c', 'v', 'x', 'p', 's', 'tab'].includes(key)) {
        event.preventDefault();
        emit('keyboard_shortcut', 84);
      }
    };
    const onWheel = (event: WheelEvent) => {
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        emit('attempted_zoom', 88);
      }
    };
    const onTouchMove = (event: TouchEvent) => {
      if (event.touches.length > 1) {
        event.preventDefault();
        emit('attempted_zoom', 70);
      }
    };

    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    document.addEventListener('fullscreenchange', onFullscreen);
    document.addEventListener('copy', onCopy);
    document.addEventListener('paste', onPaste);
    document.addEventListener('contextmenu', onContext);
    document.addEventListener('keydown', onKey);
    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('touchmove', onTouchMove, { passive: false });

    return () => {
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('fullscreenchange', onFullscreen);
      document.removeEventListener('copy', onCopy);
      document.removeEventListener('paste', onPaste);
      document.removeEventListener('contextmenu', onContext);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('touchmove', onTouchMove);
    };
  }, [emit, enabled]);

  const requestFullscreen = useCallback(async () => {
    await initializeAudio();
    if (!document.fullscreenElement && document.documentElement.requestFullscreen) {
      await document.documentElement.requestFullscreen();
    }
    const failed = !document.fullscreenElement;
    setFullscreenRequired(failed);
    return !failed;
  }, [initializeAudio]);

  return {
    warningCount,
    latestWarning,
    muted,
    audioStatus,
    fullscreenRequired,
    requestFullscreen,
    unlockAudio: initializeAudio,
    toggleMute: () => setMuted((value) => !value)
  };
}

function severityFor(type: IncidentType) {
  return type === 'fullscreen_exit' || type === 'attempted_zoom' ? 'high' : 'medium';
}
